import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, ShieldAlert, ScanSearch, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmtDate = (s) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

export const GdprRetention = () => {
  const [leadMonths, setLeadMonths] = useState(12);
  const [customerYears, setCustomerYears] = useState(6);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [preview, setPreview] = useState(null);

  const runDryRun = async () => {
    setLoading(true);
    try {
      const r = await api.get('/retention/dry-run', { params: { lead_months: leadMonths, customer_years: customerYears } });
      setPreview(r.data);
      toast.success(`Dry-run complete: ${r.data.leads.count} leads · ${r.data.customers.count} customers`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dry-run failed');
    } finally {
      setLoading(false);
    }
  };

  const runPurge = async () => {
    if (!preview) {
      toast.error('Run the dry-run first');
      return;
    }
    if (preview.leads.count === 0 && preview.customers.count === 0) {
      toast('Nothing to purge');
      return;
    }
    const phrase = `PURGE ${preview.leads.count + preview.customers.count}`;
    const answer = window.prompt(
      `You are about to PERMANENTLY:\n` +
      `  • DELETE ${preview.leads.count} unconverted lead(s) older than ${leadMonths} months\n` +
      `  • ANONYMISE ${preview.customers.count} archived customer(s) older than ${customerYears} years\n\n` +
      `Financial records (invoices, contract packs, audit log) are NOT affected.\n\n` +
      `Type:  ${phrase}\nto confirm.`
    );
    if (answer !== phrase) {
      toast('Cancelled');
      return;
    }
    setPurging(true);
    try {
      const r = await api.post('/retention/purge', null, { params: { lead_months: leadMonths, customer_years: customerYears } });
      toast.success(
        `Purge complete: ${r.data.leads_deleted} lead(s) deleted, ${r.data.customers_anonymised} customer(s) anonymised`
      );
      // Refresh dry-run state
      setPreview(null);
      runDryRun();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  useEffect(() => { runDryRun(); /* eslint-disable-next-line */ }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="gdpr-retention-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-600" /> Data Retention &amp; Purge
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Enforces the UK&nbsp;GDPR retention policy documented in our compliance statement. Marketing leads that never converted are deleted; customers in a terminal status that exceed the long-term retention window are anonymised (financial records preserved for HMRC).
            </p>
          </div>
        </div>

        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Retention thresholds
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1 block">Lead retention (months)</label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={leadMonths}
                  onChange={(e) => setLeadMonths(parseInt(e.target.value || '12', 10))}
                  data-testid="lead-months-input"
                />
                <p className="text-xs text-slate-500 mt-1">Default 12mo per ICO direct-marketing guidance.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-600 font-semibold mb-1 block">Customer retention (years)</label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={customerYears}
                  onChange={(e) => setCustomerYears(parseInt(e.target.value || '6', 10))}
                  data-testid="customer-years-input"
                />
                <p className="text-xs text-slate-500 mt-1">Default 6 years per UK HMRC requirement.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={runDryRun} disabled={loading} variant="outline" data-testid="dry-run-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ScanSearch className="w-4 h-4 mr-2" />}
                Preview (dry-run)
              </Button>
              <Button
                onClick={runPurge}
                disabled={purging || !preview || (preview.leads.count === 0 && preview.customers.count === 0)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="execute-purge-btn"
              >
                {purging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Execute purge
              </Button>
            </div>
          </CardContent>
        </Card>

        {preview && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Leads to delete</span>
                  <Badge variant={preview.leads.count > 0 ? 'destructive' : 'secondary'} data-testid="leads-count">
                    {preview.leads.count}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {preview.leads.count === 0 ? (
                  <p className="text-sm text-slate-500">No stale leads — nothing to purge.</p>
                ) : (
                  <div className="space-y-1 max-h-96 overflow-auto text-xs">
                    {preview.leads.sample.map((l) => (
                      <div key={l.id} className="border-b border-slate-100 py-1.5 flex items-center justify-between gap-2">
                        <div className="truncate">
                          <span className="font-medium text-slate-900">{l.name || l.email || '(no name)'}</span>
                          <span className="text-slate-500 ml-2">{l.source}</span>
                        </div>
                        <span className="text-slate-400 whitespace-nowrap">{fmtDate(l.created_at)}</span>
                      </div>
                    ))}
                    {preview.leads.count > preview.leads.sample.length && (
                      <p className="text-xs text-slate-400 italic pt-2">…and {preview.leads.count - preview.leads.sample.length} more</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span>Customers to anonymise</span>
                  <Badge variant={preview.customers.count > 0 ? 'destructive' : 'secondary'} data-testid="customers-count">
                    {preview.customers.count}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {preview.customers.count === 0 ? (
                  <p className="text-sm text-slate-500">No customers exceed the {customerYears}-year retention window.</p>
                ) : (
                  <div className="space-y-1 max-h-96 overflow-auto text-xs">
                    {preview.customers.sample.map((c) => (
                      <div key={c.id} className="border-b border-slate-100 py-1.5 flex items-center justify-between gap-2">
                        <div className="truncate">
                          <span className="font-medium text-slate-900">{c.name || c.email || '(no name)'}</span>
                          <Badge variant="outline" className="ml-2 text-[10px]">{c.status}</Badge>
                        </div>
                        <span className="text-slate-400 whitespace-nowrap">{fmtDate(c.last_updated)}</span>
                      </div>
                    ))}
                    {preview.customers.count > preview.customers.sample.length && (
                      <p className="text-xs text-slate-400 italic pt-2">…and {preview.customers.count - preview.customers.sample.length} more</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GdprRetention;
